import axios, { isAxiosError } from "axios";
import { buildAxiosAuthenticationHeaders } from "../util/build-axios-authentication-headers";
import { dashboardsUrlEnvVar } from "@src/cloud-connect/feature-toggles/feature-toggles";
import { Widget, WidgetPositioning } from "@src/types/dashboards";

/** Clone an existing widget on a dashboard. The BE pulls the source spec
 *  (id, type, query, encoding, presentation, image_url) server-side from
 *  the `widgetId` in the URL, then places the clone at the supplied
 *  positioning. Wire shape is application/json (NOT multipart — distinct
 *  from POST /widgets, which is multipart because it carries an optional
 *  `image` upload). The placement is forwarded as a `widget_positioning`
 *  JSON field; no `widget_data`, no `image`.
 *
 *  Caller sources `positioning` from the dashboard reducer
 *  (`selectedDashboard.positioning[<sourceWidgetId>]`) — GridStack is the
 *  source of truth for live canvas coordinates; the BE decides final
 *  placement (per doc §2.5.1 the copy goes below the original).
 *
 *  Returns the duplicated widget: server-assigned `id` + the final
 *  `positioning` chosen by the BE. */
export const postDuplicateWidgetV2 = async (
  namespace: string,
  dashboardId: string,
  widgetId: string,
  positioning: WidgetPositioning,
): Promise<{ data: Widget }> => {
  const url = dashboardsUrlEnvVar();
  const headers = buildAxiosAuthenticationHeaders();

  try {
    const response = await axios.post<Widget>(
      `${url}/api/v2/ns/${namespace}/dashboards/${dashboardId}/widgets/${widgetId}/duplicate`,
      { widget_positioning: positioning },
      { headers },
    );
    return response;
  } catch (err) {
    if (isAxiosError(err)) {
      console.error("Axios error:", err);
      throw err;
    }
    throw new Error(
      "An unexpected error occurred while duplicating the widget.",
    );
  }
};
